import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Play } from 'lucide-react';
import { getStreamingContent } from '../api';
import IframeModal from '../components/IframeModal';

const StreamingDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showIframe, setShowIframe] = useState(false);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const allContent = await getStreamingContent();
        const item = allContent.find(c => c.id === id);
        setContent(item);
      } catch (error) {
        console.error('Error fetching streaming content:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-xl">Content not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-950 py-6 px-6">
        <div className="max-w-7xl mx-auto">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            className="text-white hover:bg-white/10 mb-4"
          >
            <ArrowLeft className="mr-2" size={20} />
            Kembali
          </Button>
          <h1 className="text-4xl md:text-5xl font-bold">{content.title}</h1>
          {content.subtitle && (
            <p className="text-xl mt-2 text-blue-200">{content.subtitle}</p>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid md:grid-cols-2 gap-12">
          {/* Image/Poster */}
          <div>
            <div className="relative rounded-lg overflow-hidden shadow-2xl mb-6">
              <img
                src={content.image}
                alt={content.title}
                className="w-full h-auto object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-center justify-center">
                <div className="bg-blue-600/80 backdrop-blur-sm rounded-full p-6">
                  <Play size={48} className="text-white" />
                </div>
              </div>
            </div>

            {/* Video preview if available */}
            {content.video && (
              <div className="rounded-lg overflow-hidden shadow-2xl">
                <video
                  src={content.video}
                  controls
                  className="w-full"
                  poster={content.image}
                />
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-4">Tentang</h2>
              <p className="text-gray-300 leading-relaxed whitespace-pre-wrap">
                {content.description}
              </p>
            </div>

            <div className="mb-8">
              <div className="bg-blue-900/50 backdrop-blur-sm rounded-lg p-6 border border-blue-800">
                <h3 className="text-xl font-bold mb-3">Informasi</h3>
                <div className="space-y-2 text-gray-300">
                  <p><strong>Judul:</strong> {content.title}</p>
                  {content.subtitle && <p><strong>Status:</strong> {content.subtitle}</p>}
                  <p><strong>Platform:</strong> Streaming</p>
                </div>
              </div>
            </div>

            <Button
              onClick={() => setShowIframe(true)}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white text-lg py-6 rounded-lg"
            >
              <Play className="mr-2" size={24} />
              Tonton Sekarang
            </Button>
          </div>
        </div>
      </div>

      {/* Iframe Modal */}
      <IframeModal
        isOpen={showIframe}
        onClose={() => setShowIframe(false)}
        url={content.link}
        title={content.title}
      />
    </div>
  );
};

export default StreamingDetail;
